import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import ProtectedRoute from './components/ProtectedRoute';
import Shell from './v2/Shell';
import UpgradeModal from './components/UpgradeModal';
import './App.css';

/**
 * Trasy ładowane leniwie — login nie ciągnie recharts ani całego panelu,
 * a każda podstrona to osobny chunk (szybszy pierwszy paint).
 */
const Login = lazy(() => import('./pages/v2/LoginV2'));
const Dashboard = lazy(() => import('./pages/v2/DashboardV2'));
const Campaigns = lazy(() => import('./pages/v2/CampaignsV2'));
const CampaignDetail = lazy(() => import('./pages/v2/CampaignDetailV2'));
const CampaignEdit = lazy(() => import('./pages/v2/CampaignEditV2'));
const Stats = lazy(() => import('./pages/v2/StatsV2'));
const UsersPage = lazy(() => import('./pages/v2/UsersV2'));
const ClickLogs = lazy(() => import('./pages/v2/ClickLogsV2'));
const Tools = lazy(() => import('./pages/v2/ToolsV2'));
const Conversions = lazy(() => import('./pages/v2/ConversionsV2'));
const CampaignGroups = lazy(() => import('./pages/v2/CampaignGroupsV2'));
const ExportImport = lazy(() => import('./pages/v2/ExportImportV2'));
const ApiKeys = lazy(() => import('./pages/v2/ApiKeysV2'));
const ExchangeRates = lazy(() => import('./pages/v2/ExchangeRatesV2'));
const IPWhitelist = lazy(() => import('./pages/v2/IPWhitelistV2'));
const AdvancedRules = lazy(() => import('./pages/AdvancedRules'));
const VariantsLists = lazy(() => import('./pages/VariantsLists'));
const IPLists = lazy(() => import('./pages/IPLists'));
const CountryGroups = lazy(() => import('./pages/CountryGroups'));
const ContentTemplates = lazy(() => import('./pages/ContentTemplates'));
const Forces = lazy(() => import('./pages/Forces'));
const ABTests = lazy(() => import('./pages/ABTests'));
const PostbackProfiles = lazy(() => import('./pages/PostbackProfiles'));

function PageFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div className="v2-spinner" aria-label="Loading" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
          <Router>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/*" element={
                  <ProtectedRoute>
                    <Shell>
                      <Suspense fallback={<PageFallback />}>
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/campaigns" element={<Campaigns />} />
                          <Route path="/campaigns/new" element={<CampaignEdit />} />
                          <Route path="/campaigns/:id" element={<CampaignDetail />} />
                          <Route path="/campaigns/:id/edit" element={<CampaignDetail />} />
                          <Route path="/stats" element={<Stats />} />
                          <Route path="/clicks" element={<ClickLogs />} />
                          <Route path="/tools" element={<Tools />} />
                          <Route path="/tools/variants" element={<VariantsLists />} />
                          <Route path="/tools/ip-lists" element={<IPLists />} />
                          <Route path="/tools/country-groups" element={<CountryGroups />} />
                          <Route path="/tools/content-templates" element={<ContentTemplates />} />
                          <Route path="/tools/campaign-groups" element={<CampaignGroups />} />
                          <Route path="/tools/export-import" element={<ExportImport />} />
                          <Route path="/tools/postback-profiles" element={<PostbackProfiles />} />
                          <Route path="/tools/exchange-rates" element={<ExchangeRates />} />
                          <Route path="/conversions" element={<Conversions />} />
                          <Route path="/campaigns/:campaignId/advanced-rules" element={<AdvancedRules />} />
                          <Route path="/tools/forces" element={<Forces />} />
                          <Route path="/tools/ab-tests" element={<ABTests />} />
                          <Route path="/tools/ip-whitelist" element={<IPWhitelist />} />
                          <Route path="/tools/api-keys" element={<ApiKeys />} />
                          <Route path="/users" element={
                            <ProtectedRoute requireAdmin={true}>
                              <UsersPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/ip-whitelist" element={<IPWhitelist />} />
                        </Routes>
                      </Suspense>
                    </Shell>
                  </ProtectedRoute>
                } />
              </Routes>
            </Suspense>
          </Router>
          <UpgradeModal />
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
